// src/lib/bundles-shared.ts
// Czyste (bez zależności serwerowych) typy i funkcje wyceny zestawów kursów.
// Bezpieczne do importu w komponentach klienta i w kodzie serwera.

export interface BundleCourseView {
  id: string; // courseId
  slug: string;
  title: string;
  price: number; // regularna cena pojedynczego kursu
  thumbnail: string;
  cardColor: string;
  order: number;
}

export interface BundleMaterialView {
  id: string; // materialId
  slug: string;
  title: string;
  price: number; // efektywna cena materiału (z promocją)
  thumbnail: string;
  order: number;
}

export interface BundlePricing {
  comparePrice: number; // suma regularnych cen pojedynczych kursów
  bundlePrice: number; // efektywna cena zestawu (promo lub regularna)
  savings: number; // comparePrice - bundlePrice (>= 0)
  discountPercent: number; // procent rabatu względem comparePrice
}

export interface BundleView extends BundlePricing {
  id: string;
  slug: string;
  name: string;
  description: string;
  thumbnail: string;
  badgeLabel: string;
  savingsLabel: string; // gotowy tekst marketingowy
  giftable: boolean;
  courses: BundleCourseView[];
  materials: BundleMaterialView[];
}

export function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

/** Kwota w zł — grosze tylko, gdy występują. */
export function formatPln(n: number): string {
  return Number.isInteger(n) ? `${n.toFixed(0)} zł` : `${n.toFixed(2)} zł`;
}

/** Efektywna cena zestawu: promocyjna (gdy > 0) lub regularna. */
export function bundleEffectivePrice(b: { price: number; promoPrice: number }): number {
  return b.promoPrice > 0 ? round2(b.promoPrice) : round2(b.price);
}

/** Wyliczenie cen zestawu na podstawie regularnych cen kursów. */
export function computeBundlePricing(
  bundle: { price: number; promoPrice: number },
  coursePrices: number[]
): BundlePricing {
  const comparePrice = round2(coursePrices.reduce((s, p) => s + (p || 0), 0));
  const bundlePrice = bundleEffectivePrice(bundle);
  const savings = round2(Math.max(0, comparePrice - bundlePrice));
  const discountPercent =
    comparePrice > 0 ? Math.round((savings / comparePrice) * 100) : 0;
  return { comparePrice, bundlePrice, savings, discountPercent };
}

/** Gotowy tekst „Oszczędzasz X zł" — własny (admin) lub automatyczny. */
export function resolveSavingsLabel(customLabel: string, savings: number): string {
  const custom = (customLabel || "").trim();
  if (custom) return custom;
  if (savings > 0) return `Oszczędzasz ${formatPln(savings)}`;
  return "";
}

/**
 * Rozdziela cenę zestawu na poszczególne kursy proporcjonalnie do ich cen
 * regularnych. Ostatni kurs absorbuje różnicę zaokrągleń, dzięki czemu suma
 * przydzielonych kwot dokładnie równa się cenie zestawu.
 */
export function allocateBundlePrice(coursePrices: number[], bundlePrice: number): number[] {
  const n = coursePrices.length;
  if (n === 0) return [];
  const target = round2(bundlePrice);
  const total = coursePrices.reduce((s, p) => s + (p || 0), 0);

  let alloc: number[];
  if (total <= 0) {
    const each = round2(target / n);
    alloc = coursePrices.map(() => each);
  } else {
    alloc = coursePrices.map((p) => round2(((p || 0) / total) * target));
  }

  const sum = round2(alloc.reduce((s, p) => s + p, 0));
  const diff = round2(target - sum);
  if (n > 0) alloc[n - 1] = round2(alloc[n - 1] + diff);
  return alloc;
}

/** Czy wybór kursów spełnia warunek zestawu (wszystkie kursy zaznaczone). */
export function selectionQualifiesForBundle(
  bundleCourseIds: string[],
  selectedCourseIds: string[]
): boolean {
  if (bundleCourseIds.length < 2) return false;
  const selected = new Set(selectedCourseIds);
  return bundleCourseIds.every((id) => selected.has(id));
}
